import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Shield, Eye, EyeOff, Mail, Lock, ArrowRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';

export default function Login() {
  const navigate = useNavigate();
  const { login, verifyMFA, resendOTP, pendingMFA } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // MFA state
  const [otpCode, setOtpCode] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(email, password);
      
      if (result.requiresMFA) {
        toast.success('OTP sent to your alternate email');
      } else {
        toast.success('Login successful!');
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMFA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await verifyMFA(otpCode);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid OTP';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setResendLoading(true);
    try {
      await resendOTP();
      toast.success('OTP resent successfully');
      setResendCooldown(60);
      
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to resend OTP';
      toast.error(message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpChange = (value) => {
    // Only allow numbers and max 6 digits
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(cleaned);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&q=85)'
        }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0, 0, 83, 0.95) 0%, rgba(0, 109, 115, 0.9) 100%)' }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Shield className="h-6 w-6" />
            </div>
            <div>
              <h2 className="font-bold text-xl">Polar iHealth</h2>
              <p className="text-sm text-white/70">Internal Portal</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <h1 className="text-4xl font-bold leading-tight">
              Secure Access to<br />
              Your Healthcare<br />
              Workspace
            </h1>
            <p className="text-lg text-white/80 max-w-md">
              Manage schedules, timesheets, and documents with enterprise-grade security and MFA protection.
            </p>
          </div>
          
          <p className="text-sm text-white/50">
            © 2026 Polar iHealth Clinics. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#000053' }}>
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-xl text-slate-900">Polar iHealth</h2>
              <p className="text-sm text-slate-500">Internal Portal</p>
            </div>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-slate-900">
                {pendingMFA ? 'Verify Your Identity' : 'Welcome Back'}
              </CardTitle>
              <CardDescription className="text-slate-500">
                {pendingMFA 
                  ? 'Enter the 6-digit code sent to your alternate email'
                  : 'Sign in to access the internal portal'
                }
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {!pendingMFA ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@polarihealth.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-11"
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-700">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 h-11"
                        required
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        data-testid="toggle-password"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 text-white"
                    style={{ backgroundColor: '#006D73' }}
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Sign In
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>

                  <div className="text-center mt-4">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-teal-600 hover:text-teal-700 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleVerifyMFA} className="space-y-6">
                  <div className="space-y-4">
                    <Label className="text-slate-700">Verification Code</Label>
                    <div className="flex justify-center gap-2">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <input
                          key={index}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={otpCode[index] || ''}
                          onChange={(e) => {
                            const newValue = otpCode.split('');
                            newValue[index] = e.target.value;
                            handleOtpChange(newValue.join(''));
                            
                            // Auto-focus next input
                            if (e.target.value && index < 5) {
                              const nextInput = e.target.parentElement.children[index + 1];
                              if (nextInput) nextInput.focus();
                            }
                          }}
                          onKeyDown={(e) => {
                            // Handle backspace
                            if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
                              const prevInput = e.target.parentElement.children[index - 1];
                              if (prevInput) prevInput.focus();
                            }
                          }}
                          className="otp-input"
                          data-testid={`otp-input-${index}`}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-center text-slate-500">
                      Check your alternate email for the verification code
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 text-white"
                    style={{ backgroundColor: '#006D73' }}
                    disabled={loading || otpCode.length !== 6}
                    data-testid="verify-mfa-submit"
                  >
                    {loading ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Verify & Sign In
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={resendLoading || resendCooldown > 0}
                      className="text-sm text-teal-600 hover:text-teal-700 disabled:text-slate-400"
                      data-testid="resend-otp-btn"
                    >
                      {resendLoading ? (
                        'Sending...'
                      ) : resendCooldown > 0 ? (
                        `Resend code in ${resendCooldown}s`
                      ) : (
                        "Didn't receive a code? Resend"
                      )}
                    </button>
                  </div>
                </form>
              )}

              {/* Help Text */}
              <div className="mt-6 text-center">
                <p className="text-xs text-slate-500">
                  Secure access for Polar iHealth staff and physicians.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
